import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadSimple, Trash, ArrowClockwise, FileText, Database, DownloadSimple, SignIn, SignOut, CalendarBlank, X } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORY_COLORS = {
  CREW: "bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/40",
  TRUCK: "bg-[#FACC15]/20 text-[#FACC15] border-[#FACC15]/40",
  VAN: "bg-[#32D74B]/20 text-[#32D74B] border-[#32D74B]/40",
  FACILITY: "bg-[#A855F7]/20 text-[#A855F7] border-[#A855F7]/40",
  CRAFT: "bg-[#EC4899]/20 text-[#EC4899] border-[#EC4899]/40",
  OTHER: "bg-[#262626] text-[#A3A3A3] border-[#262626]",
};

export function SecurityAdminTab() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [clearingType, setClearingType] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);
  const [exportingView, setExportingView] = useState(null);
  const [togglingDoc, setTogglingDoc] = useState(null);
  const [showDocumentsLoaded, setShowDocumentsLoaded] = useState(() => {
    const saved = localStorage.getItem("sa_show_documents_loaded");
    return saved === null ? true : saved === "true";
  });
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");

  useEffect(() => {
    localStorage.setItem("sa_show_documents_loaded", String(showDocumentsLoaded));
    window.dispatchEvent(new Event("sa-documents-loaded-visibility"));
  }, [showDocumentsLoaded]);

  const toggleDocument = async (docType, currentlyEnabled) => {
    setTogglingDoc(docType);
    // Optimistic UI update
    setDocumentTypes((types) => types.map((t) => (t.document_type === docType ? { ...t, enabled: !currentlyEnabled } : t)));
    try {
      await api.put("/security/documents/toggle", { document_type: docType, enabled: !currentlyEnabled });
      toast.success(`${docType} ${!currentlyEnabled ? "enabled" : "disabled"}`);
    } catch (err) {
      // Revert on error
      setDocumentTypes((types) => types.map((t) => (t.document_type === docType ? { ...t, enabled: currentlyEnabled } : t)));
      toast.error(formatApiError(err.response?.data?.detail) || "Toggle failed");
    } finally {
      setTogglingDoc(null);
    }
  };

  const exportCsv = async (view) => {
    setExportingView(view);
    try {
      const params = new URLSearchParams({ view });
      if (exportStartDate) params.set("start_date", exportStartDate);
      if (exportEndDate) params.set("end_date", exportEndDate);
      const response = await api.get(`/security/export.csv?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const ts = new Date().toISOString().slice(0, 10);
      const rangeSuffix = (exportStartDate || exportEndDate) ? `_${exportStartDate || "all"}_to_${exportEndDate || "all"}` : "";
      a.download = `security_${view}${rangeSuffix}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success(`Exported ${view.replace("_", " ").toUpperCase()} as CSV`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
    } finally {
      setExportingView(null);
    }
  };

  const loadDocumentTypes = useCallback(async () => {
    try {
      const { data } = await api.get("/security/document-types");
      setDocumentTypes(data.types || []);
    } catch (err) {
      console.error("doc-types load failed:", err);
    }
  }, []);

  useEffect(() => {
    loadDocumentTypes();
  }, [loadDocumentTypes]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/security/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message);
      setLastUpload({
        filename: data.filename,
        inserted: data.inserted,
        updated: data.updated,
        per_document: data.per_document || {},
        at: new Date().toLocaleString(),
      });
      await loadDocumentTypes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearDocument = async (docType) => {
    if (!window.confirm(`Delete ALL "${docType}" records? This will remove those people from the Security page and cannot be undone.`)) return;
    setClearingType(docType);
    try {
      const { data } = await api.delete(`/security/records/by-document?document_type=${encodeURIComponent(docType)}`);
      toast.success(`Cleared ${data.deleted} record(s) from ${docType}`);
      await loadDocumentTypes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally {
      setClearingType(null);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL security records AND all check-in/check-out history? This is IRREVERSIBLE.")) return;
    if (!window.confirm("Are you absolutely sure? Type OK in the next prompt to confirm.")) return;
    const confirm = window.prompt("Type OK to confirm total wipe:");
    if (confirm !== "OK") { toast.error("Cancelled"); return; }
    setClearingAll(true);
    try {
      const { data } = await api.delete("/security/records");
      toast.success(`Wiped ${data.deleted_records} records + ${data.deleted_events} events`);
      setLastUpload(null);
      await loadDocumentTypes();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Wipe failed");
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Upload Section */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <UploadSimple size={16} weight="bold" className="text-[#007AFF]" /> Upload Crew / Vehicle PDF
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#737373]">
            PDF must contain PROJECT / PRODUCTION / DOCUMENT header lines and a data table.
            Supported document types are auto-detected: CREW, TRUCK, VAN, FACILITY EQUIPMENT, CRAFT EQUIPMENT.
            Re-uploading updates existing records by natural key (no duplicates).
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" onChange={handleUpload} className="hidden" data-testid="security-admin-upload-input" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm text-xs" data-testid="security-admin-upload-btn">
              <UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading..." : "Choose PDF"}
            </Button>
            {lastUpload && (
              <div className="text-xs text-[#A3A3A3]">
                Last upload: <span className="text-white font-mono-data">{lastUpload.filename}</span> — <span className="text-[#32D74B]">{lastUpload.inserted} new</span> · <span className="text-[#FACC15]">{lastUpload.updated} updated</span> · <span className="text-[#737373]">{lastUpload.at}</span>
              </div>
            )}
          </div>
          {lastUpload && Object.keys(lastUpload.per_document).length > 0 && (
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#737373] mb-2">Rows ingested per document</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(lastUpload.per_document).map(([doc, count]) => (
                  <span key={doc} className="px-2 py-1 rounded-sm text-[10px] bg-[#121212] border border-[#262626] text-[#A3A3A3]">
                    <span className="text-white font-mono-data">{count}</span> · {doc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Section */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <DownloadSimple size={16} weight="bold" className="text-[#32D74B]" /> Export Check-In / Check-Out Log
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#737373]">Download the complete history of check-in or check-out events as a CSV file (opens in Excel / Google Sheets). Timestamps are exported as separate <span className="text-white">Date</span> and <span className="text-white">Time</span> columns.</p>
          <div className="flex items-end gap-3 flex-wrap p-3 bg-[#0A0A0A] border border-[#262626] rounded-sm">
            <CalendarBlank size={16} weight="bold" className="text-[#007AFF] mb-2" />
            <div className="space-y-1">
              <Label htmlFor="export-start" className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">From date</Label>
              <Input id="export-start" type="date" value={exportStartDate} onChange={(e) => setExportStartDate(e.target.value)}
                className="bg-[#121212] border-[#262626] text-white rounded-sm text-xs h-8 w-[160px] font-mono-data"
                data-testid="sa-export-start-date" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="export-end" className="text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]">To date</Label>
              <Input id="export-end" type="date" value={exportEndDate} onChange={(e) => setExportEndDate(e.target.value)}
                className="bg-[#121212] border-[#262626] text-white rounded-sm text-xs h-8 w-[160px] font-mono-data"
                data-testid="sa-export-end-date" />
            </div>
            {(exportStartDate || exportEndDate) && (
              <Button variant="ghost" size="sm" onClick={() => { setExportStartDate(""); setExportEndDate(""); }}
                className="text-[#737373] hover:text-white text-[10px] h-8"
                data-testid="sa-export-clear-dates">
                <X size={12} className="mr-1" /> Clear
              </Button>
            )}
            {!exportStartDate && !exportEndDate && (
              <span className="text-[10px] text-[#737373] pb-2">Leave blank to export all events</span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={() => exportCsv("total_in")} disabled={exportingView === "total_in"}
              className="bg-[#32D74B]/20 hover:bg-[#32D74B]/40 text-[#32D74B] border border-[#32D74B]/40 rounded-sm text-xs"
              data-testid="sa-export-total-in">
              <SignIn size={14} className="mr-1" /> {exportingView === "total_in" ? "Exporting..." : "Export TOTAL IN (CSV)"}
              <DownloadSimple size={12} className="ml-2" />
            </Button>
            <Button onClick={() => exportCsv("total_out")} disabled={exportingView === "total_out"}
              className="bg-[#FF3B30]/20 hover:bg-[#FF3B30]/40 text-[#FF3B30] border border-[#FF3B30]/40 rounded-sm text-xs"
              data-testid="sa-export-total-out">
              <SignOut size={14} className="mr-1" /> {exportingView === "total_out" ? "Exporting..." : "Export TOTAL OUT (CSV)"}
              <DownloadSimple size={12} className="ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Loaded Documents Overview */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
              <Database size={16} weight="bold" className="text-[#32D74B]" /> Loaded Documents
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2" title={showDocumentsLoaded ? "Hide loaded documents list" : "Show loaded documents list"}>
                <span className={`text-[10px] uppercase tracking-wider ${showDocumentsLoaded ? "text-[#32D74B]" : "text-[#737373]"}`}>
                  {showDocumentsLoaded ? "SHOW" : "HIDE"}
                </span>
                <Switch
                  checked={showDocumentsLoaded}
                  onCheckedChange={setShowDocumentsLoaded}
                  data-testid="sa-toggle-documents-loaded-section"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={loadDocumentTypes} className="text-[#737373] hover:text-white text-xs h-7">
                <ArrowClockwise size={12} className="mr-1" /> Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        {showDocumentsLoaded && (
        <CardContent>
          {documentTypes.length === 0 ? (
            <div className="text-center py-8">
              <FileText size={48} weight="thin" className="mx-auto text-[#404040]" />
              <p className="text-sm text-[#737373] mt-2">No documents uploaded yet. Upload a PDF above to get started.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documentTypes.map((t) => (
                <div key={t.document_type}
                  className={`flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#262626] rounded-sm ${t.enabled === false ? "opacity-50" : ""}`}
                  data-testid={`sa-doc-${t.document_category}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border ${CATEGORY_COLORS[t.document_category] || CATEGORY_COLORS.OTHER}`}>{t.document_category}</span>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{t.document_type}</p>
                      <p className="text-[10px] text-[#737373] truncate">Project: {t.project || "\u2014"} · Production: {t.production || "\u2014"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="flex items-center gap-2" title={t.enabled === false ? "Disabled — hidden from Security page" : "Enabled — visible in Security search"}>
                      <span className={`text-[10px] uppercase tracking-wider ${t.enabled === false ? "text-[#FF3B30]" : "text-[#32D74B]"}`}>
                        {t.enabled === false ? "OFF" : "ON"}
                      </span>
                      <Switch
                        checked={t.enabled !== false}
                        disabled={togglingDoc === t.document_type}
                        onCheckedChange={() => toggleDocument(t.document_type, t.enabled !== false)}
                        data-testid={`sa-toggle-${t.document_category}`}
                      />
                    </div>
                    <span className="text-lg font-heading font-bold text-white font-mono-data">{t.count}</span>
                    {isSuperadmin && (
                      <Button onClick={() => clearDocument(t.document_type)} disabled={clearingType === t.document_type}
                        size="sm" variant="outline"
                        className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm text-[10px] h-7"
                        data-testid={`sa-clear-${t.document_category}`}>
                        <Trash size={11} className="mr-1" /> {clearingType === t.document_type ? "Deleting..." : "Delete"}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Danger Zone */}
      {isSuperadmin && documentTypes.length > 0 && (
        <Card className="bg-[#121212] border-[#FF3B30]/30 rounded-sm shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm font-semibold text-[#FF3B30] uppercase tracking-wider flex items-center gap-2">
              <Trash size={16} weight="bold" /> Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-[#A3A3A3]">
              Permanently delete <span className="text-white font-semibold">ALL</span> security records AND check-in/check-out history. Cannot be undone.
            </p>
            <Button onClick={clearAll} disabled={clearingAll}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm text-xs" data-testid="sa-wipe-all">
              <Trash size={14} className="mr-1" /> {clearingAll ? "Wiping..." : "Wipe ALL Security Data"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
